/* The hero visual: a faithful FDA Nutrition Facts panel, hand-built in HTML/CSS.
   The values are not invented — they are the label engine's own output for the
   FDA's published 2/3-cup granola example (probed through `buildPanel` +
   `sampleFieldValues()` on 2026-08-09, including the rounding nuance that
   8 mg of iron displays as 45% DV because %DV is computed from the unrounded
   analytical value). So the motif is the product telling the truth about itself.
   Pure markup + the design-system CSS. No images, no JS. */
import { Download, Scale, Search } from "./icons";

const MACROS: Array<{
  name: string;
  amt: string;
  pdv: string | null;
  bold?: boolean;
  indent?: 1 | 2;
}> = [
  { name: "Total Fat", amt: "8g", pdv: "10%", bold: true },
  { name: "Saturated Fat", amt: "1g", pdv: "5%", indent: 1 },
  { name: "Trans Fat", amt: "0g", pdv: null, indent: 1 },
  { name: "Cholesterol", amt: "0mg", pdv: "0%", bold: true },
  { name: "Sodium", amt: "160mg", pdv: "7%", bold: true },
  { name: "Total Carbohydrate", amt: "37g", pdv: "13%", bold: true },
  { name: "Dietary Fiber", amt: "4g", pdv: "14%", indent: 1 },
  { name: "Total Sugars", amt: "12g", pdv: null, indent: 1 },
  { name: "Includes 10g Added Sugars", amt: "", pdv: "20%", indent: 2 },
  { name: "Protein", amt: "3g", pdv: null, bold: true },
];

const MICROS: Array<{ name: string; amt: string; pdv: string }> = [
  { name: "Vitamin D", amt: "2 mcg", pdv: "10%" },
  { name: "Calcium", amt: "260 mg", pdv: "20%" },
  { name: "Iron", amt: "8 mg", pdv: "45%" },
  { name: "Potassium", amt: "235 mg", pdv: "6%" },
];

function FlowArrow() {
  return (
    <svg
      className="nm-flow-arrow"
      width="26"
      height="34"
      viewBox="0 0 26 34"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M13 2v26" strokeDasharray="3 4" />
      <path d="m6 22 7 8 7-8" />
    </svg>
  );
}

export function NutritionPanelMotif() {
  return (
    <div
      className="nm-motif"
      role="img"
      aria-label="A Nutrition Facts panel for the FDA's granola example — 2/3 cup servings, 230 calories — with 21 CFR 101.9 rounding applied, exported as a print-ready 300 DPI PNG."
    >
      <div className="nm-motif__badges" aria-hidden>
        <span className="nm-motif__badge">
          <Scale width={15} height={15} />
          21 CFR 101.9 rounding
        </span>
        <span className="nm-motif__badge">
          <Search width={15} height={15} />
          USDA FoodData Central
        </span>
      </div>

      <article className="nm-panel" aria-hidden>
        <p className="nm-panel__title">Nutrition Facts</p>
        <p className="nm-panel__servings">8 servings per container</p>
        <div className="nm-panel__serving">
          <span>Serving size</span>
          <span>2/3 cup (55g)</span>
        </div>
        <div className="nm-panel__bar nm-panel__bar--heavy" />
        <div className="nm-panel__aps">
          <span>Amount per serving</span>
          <span>% Daily Value*</span>
        </div>
        <div className="nm-panel__cal">
          <span className="nm-panel__cal-label">Calories</span>
          <span className="nm-panel__cal-num">230</span>
        </div>
        <div className="nm-panel__bar nm-panel__bar--mid" />
        <ul className="nm-panel__rows">
          {MACROS.map((r) => (
            <li
              key={r.name}
              className={[
                "nm-panel__row",
                r.bold ? "nm-panel__row--bold" : "",
                r.indent === 1 ? "nm-panel__row--i1" : "",
                r.indent === 2 ? "nm-panel__row--i2" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="nm-panel__name">
                {r.name}
                {r.amt ? ` ${r.amt}` : ""}
              </span>
              {r.pdv ? <span className="nm-panel__pdv">{r.pdv}</span> : null}
            </li>
          ))}
        </ul>
        <ul className="nm-panel__micros">
          {MICROS.map((m) => (
            <li key={m.name}>
              <span>
                {m.name} {m.amt}
              </span>
              <span className="nm-panel__pdv">{m.pdv}</span>
            </li>
          ))}
        </ul>
        <p className="nm-panel__foot">
          * The % Daily Value (DV) tells you how much a nutrient in a serving of
          food contributes to a daily diet. 2,000 calories a day is used for
          general nutrition advice.
        </p>
      </article>

      <div className="nm-motif__flow">
        <FlowArrow />
        <div className="nm-export">
          <span className="nm-export__icon" aria-hidden>
            <Download width={20} height={20} />
          </span>
          <span className="nm-export__meta">
            <b>granola-label.png</b>
            <span>300 DPI PNG · saved to the product</span>
          </span>
          <span className="nm-chip nm-chip--green" style={{ marginLeft: "auto" }}>
            <span className="nm-chip__dot" />
            Exported
          </span>
        </div>
      </div>
    </div>
  );
}
